/**
 * FolderPlanPreviewView · 文件夹方案预览的纯 UI 视图
 *
 * 完全数据化, 无 store / 无网络副作用; 由实时模式 / 历史模式各自的 hook
 * 把数据归一后传入。可单独传 props 在 Storybook / 单元测试里渲染。
 *
 * 结构 (从上到下):
 *   1. 标题「分类方案预览」
 *   2. 顶部维度链摘要条 (layersSummary 为空时整条隐藏, 历史阶段 2 暂时为空)
 *   3. 文件夹方案树 (loading / error / 空 / 节点列表)
 *   4. 底部操作条 (仅 editable 形态; 只读 / 已建夹时 actionBar 为 null 不渲染)
 */

import React from 'react'
import { Checkbox, Spin, Button } from 'antd'
import classNames from 'classnames'
import { RightOutlined } from '@ant-design/icons'
import type { DimensionType, LayerSummary } from '../../../../types/ai-classify'
import type {
  ActionBarConfig,
  FolderPlanPreviewMode,
  FolderPlanPreviewViewProps,
  PlanPreviewNode,
  SelectAllConfig
} from './types'
import type { PaywallBanner } from '../../../components/FolderPaywall/paywall'
import PlanFolderRow from './PlanFolderRow'
import styles from './FolderPlanPreview.module.scss'

/** 维度类型 → 中文展示名 · 顶部摘要条用 */
const DIMENSION_LABEL: Record<DimensionType, string> = {
  time: '时间',
  department: '部门',
  member: '成员',
  watermark: '水印',
  // 兜底用 · 正常走 customerVisitLabel(团队自定义 ${noun}${verb})，仅配置缺失时回落到此
  customer_visit: '客户拜访',
  custom: '关键词'
}

/**
 * 解析维度展示名
 *  · customer_visit 优先用团队自定义的 ${noun}${verb}(customerVisitLabel)，
 *    缺配置时回落到 DIMENSION_LABEL 的「客户拜访」
 *  · 其余维度查 DIMENSION_LABEL，未命中再兜底原始 dimension 字符串
 */
const resolveDimensionLabel = (
  layer: LayerSummary,
  customerVisitLabel?: string
): string => {
  if (layer.dimension === 'customer_visit' && customerVisitLabel) {
    return customerVisitLabel
  }
  return DIMENSION_LABEL[layer.dimension] || layer.dimension
}

/** 只读态判定 · 已确认创建 (live_readonly) 或 历史只读 (history_readonly) */
const isReadonlyMode = (mode: FolderPlanPreviewMode): boolean =>
  mode === 'live_readonly' || mode === 'history_readonly'

const FolderPlanPreviewView: React.FC<FolderPlanPreviewViewProps> = ({
  mode,
  readonlyReason,
  layersSummary,
  customerVisitLabel,
  expectedTotal,
  roots,
  expandedKeys,
  loadingKeys,
  excludedKeys,
  loading,
  error,
  actionBar,
  banner,
  selectAll,
  onToggleExpand,
  onToggleExclude
}) => {
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.title}>分类方案预览</div>
        {/* "已确认"只标真的建过夹的卡（readonlyReason=confirmed）；
            被新方案取代（superseded）/ 老数据（unknown）只置灰不标记，
            置灰本身由 mode 驱动（treeReadonly + 不渲染按钮/勾选框） */}
        {isReadonlyMode(mode) && readonlyReason === 'confirmed' && (
          <div className={styles.subtitle}>已确认</div>
        )}
      </div>

      {layersSummary.length > 0 && (
        <ChainBar
          layersSummary={layersSummary}
          expectedTotal={expectedTotal}
          customerVisitLabel={customerVisitLabel}
        />
      )}

      {/* 全选/取消全选 · 仅可编辑态（hook 决策传入）且树已就绪时渲染 */}
      {selectAll && !loading && !error && roots.length > 0 && (
        <SelectAllBar config={selectAll} />
      )}

      <TreeSection
        loading={loading}
        error={error}
        roots={roots}
        mode={mode}
        expandedKeys={expandedKeys}
        loadingKeys={loadingKeys}
        excludedKeys={excludedKeys}
        onToggleExpand={onToggleExpand}
        onToggleExclude={onToggleExclude}
      />

      {banner && <PaywallBannerView banner={banner} />}

      {actionBar && <ActionBar config={actionBar} />}
    </div>
  )
}

// ─── 付费卡点 banner ─────────────────────────────────────

const PaywallBannerView: React.FC<{ banner: PaywallBanner }> = ({ banner }) => {
  if (banner.kind === 'novip') {
    return (
      <div className={styles.paywallBanner}>
        <div className={styles.paywallNovip}>
          AI批量分类是高级会员功能，开通会员即可创建
        </div>
      </div>
    )
  }
  return (
    <div className={styles.paywallBanner}>
      <div className={styles.paywallCapacity}>
        创建后占用容量{' '}
        <span className={styles.paywallOver}>{banner.newTotal}</span>
        <span className={styles.paywallCapTotal}>/{banner.cap}</span>
      </div>
      <div className={styles.paywallUpsell}>
        已超出{banner.tierName}上限，升级会员获得更多
      </div>
    </div>
  )
}

// ─── 全选/取消全选行 ─────────────────────────────────────

const SelectAllBar: React.FC<{ config: SelectAllConfig }> = ({ config }) => (
  <div className={styles.selectAllBar}>
    <Checkbox
      checked={config.checked}
      indeterminate={config.indeterminate}
      onChange={config.onToggle}
    >
      <span className={styles.selectAllLabel}>全选</span>
    </Checkbox>
  </div>
)

// ─── 顶部摘要条 ──────────────────────────────────────────

interface ChainBarProps {
  layersSummary: LayerSummary[]
  expectedTotal: number
  /** 客户拜访维度展示名(团队自定义 ${noun}${verb}) · 缺省时回落 DIMENSION_LABEL */
  customerVisitLabel?: string
}

const ChainBar: React.FC<ChainBarProps> = ({
  layersSummary,
  expectedTotal,
  customerVisitLabel
}) => (
  <div className={styles.chainBar}>
    <span className={styles.chainLabel}>分类维度</span>
    <span className={styles.chainPath}>
      {layersSummary.map((l, idx) => (
        <React.Fragment key={l.layer_id}>
          {idx > 0 && <RightOutlined className={styles.chainSep} />}
          <span className={styles.chainNode}>
            {resolveDimensionLabel(l, customerVisitLabel)}
            <span className={styles.chainCount}>{l.value_count}</span>
          </span>
        </React.Fragment>
      ))}
    </span>
    <span className={styles.chainTotal}>预计 {expectedTotal} 个文件夹</span>
  </div>
)

// ─── 树渲染 ──────────────────────────────────────────────

interface TreeSectionProps {
  loading: boolean
  error: string | null
  roots: PlanPreviewNode[]
  mode: FolderPlanPreviewMode
  expandedKeys: Set<string>
  loadingKeys: Set<string>
  excludedKeys: Set<string>
  onToggleExpand: (key: string, node: PlanPreviewNode) => void
  onToggleExclude: (key: string) => void
}

const TreeSection: React.FC<TreeSectionProps> = ({
  loading,
  error,
  roots,
  mode,
  expandedKeys,
  loadingKeys,
  excludedKeys,
  onToggleExpand,
  onToggleExclude
}) => {
  if (loading) {
    return (
      <div className={styles.stateBox}>
        <Spin tip="加载预览…" />
      </div>
    )
  }
  if (error) {
    return (
      <div className={styles.errorText}>
        <span>{error}</span>
      </div>
    )
  }
  if (roots.length === 0) {
    return <div className={styles.emptyTree}>（暂无可预览的文件夹）</div>
  }
  return (
    <div
      className={classNames(styles.tree, {
        [styles.treeReadonly]: isReadonlyMode(mode)
      })}
    >
      {roots.map((node) => (
        <PlanFolderRow
          key={node.key}
          node={node}
          depth={0}
          mode={mode}
          expandedKeys={expandedKeys}
          loadingKeys={loadingKeys}
          excludedKeys={excludedKeys}
          ancestorExcluded={false}
          onToggleExpand={onToggleExpand}
          onToggleExclude={onToggleExclude}
        />
      ))}
    </div>
  )
}

// ─── 底部操作条 ──────────────────────────────────────────

const ActionBar: React.FC<{ config: ActionBarConfig }> = ({ config }) => {
  const {
    editable,
    selectedCount,
    totalCount,
    confirming,
    hasFolders,
    confirmText,
    onConfirm
  } = config
  return (
    <div className={styles.actionBar}>
      <div className={styles.actionLeft}>
        {totalCount > 0 && (
          <span className={styles.countHint}>
            已选 {selectedCount} / 共 {totalCount}
          </span>
        )}
      </div>
      {editable && (
        <div className={styles.actionRight}>
          {/* 0 选中禁用 · "取消全选"一键即可到 0，确认空方案无意义 */}
          <Button
            type="primary"
            loading={confirming}
            disabled={!hasFolders || selectedCount === 0}
            onClick={onConfirm}
          >
            {confirmText}
          </Button>
        </div>
      )}
    </div>
  )
}

export default FolderPlanPreviewView
